import axios from 'axios';
import * as Sentry from '@sentry/react';
import { StatusCodes } from 'http-status-codes';
import { iframeLocalStorage } from 'chat-ui/src/utils/iframeLocalStorage';
import { ERROR_MESSAGES } from '@common/constants';
import { getErrorMessage, getErrorStatus } from '@common/utils/getErrorDetails';
import { ChatHistory } from '@/types';

const ERROR_STATUS = {
  METADATA_MISSING: 434,
  TEST_CONVERSATION_NOT_FOUND: 426,
};

const path = window.location.pathname;
const pathParts = path.split('/');
const url_project_id = pathParts[1];

const axiosInstanceChat = axios.create({
  baseURL: import.meta.env.VITE_CHAT_URL,
});

axiosInstanceChat.interceptors.request.use((config) => {
  // Add client IP to all requests
  const clientIP = iframeLocalStorage.getItem('client_ip');
  if (clientIP) {
    config.headers['x-client-ip'] = clientIP;
  }
  return config;
});

axiosInstanceChat.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorStatus = getErrorStatus(error);
    const message = getErrorMessage(error);

    // Filter out known non-critical errors from Sentry
    const filteredMessages = [
      ERROR_MESSAGES.PROJECT_IS_COMPLETED,
      ERROR_MESSAGES.PROJECT_NOT_PUBLISHED,
      ERROR_MESSAGES.PROJECT_RESPONDENT_EXCEEDED,
    ];

    // Check if message starts with any filtered message (handles messages with appended data like "error||link")
    const shouldExcludeFromSentry = filteredMessages.some(filtered => message.startsWith(filtered));

    // Only send to Sentry if not filtered
    if (!shouldExcludeFromSentry) {
      // Capture API errors with additional context
      Sentry.withScope((scope) => {
        // Add request context
        scope.setTag('request_url', error.config?.url || 'unknown');
        scope.setTag('request_method', error.config?.method || 'unknown');

        // Add response context
        scope.setTag('response_status', errorStatus);

        // Create status tag for consistent reuse in fingerprinting
        const statusTag = `status-${errorStatus}`;

        // Add user/session context with both project and conversation IDs
        const conversationId = iframeLocalStorage.getItem(
          `current_conversation_id${path.includes('/sandbox') ? '/sandbox' : ''}/${url_project_id}`,
        );
        if (conversationId) {
          scope.setTag('conversation_id', conversationId);
          scope.setTag('project_id', url_project_id);
        }

        // Add browser/device context
        scope.setContext('browser_info', {
          userAgent: navigator.userAgent,
          language: navigator.language,
          screenSize: `${window.screen.width}x${window.screen.height}`,
          viewportSize: `${window.innerWidth}x${window.innerHeight}`,
          connection: navigator.connection ? navigator.connection.effectiveType : 'unknown',
        });

        // Set appropriate error category
        scope.setTag('error_type', 'api_error');

        // Custom fingerprinting based on error type
        const url = error.config?.url || '';

        // Group API errors by endpoint and status
        if (url.includes('/chat/history')) {
          scope.setFingerprint(['chat-history-api-error', statusTag]);
        } else if (url.includes('/chat/published') || url.includes('/chat/sandbox')) {
          scope.setFingerprint(['chat-initialization-api-error', statusTag]);
        } else if (url.includes('/chat/reset')) {
          scope.setFingerprint(['chat-reset-api-error', statusTag]);
        } else {
          // Default fingerprinting for API errors
          scope.setFingerprint(['chat-api-error', `method-${error.config?.method}`, statusTag]);
        }

        // Capture the exception with enhanced context
        Sentry.captureException(error);
      });
    }

    // Handle specific error cases
    // metadata missing
    if (errorStatus === ERROR_STATUS.METADATA_MISSING) {
      // No navigation logging
      window.location.href = '/chat/error-page';
    }

    // conversation missing
    if (errorStatus === ERROR_STATUS.TEST_CONVERSATION_NOT_FOUND) {
      // No session state logging
      iframeLocalStorage.removeItem(`project_consent/${url_project_id}/sandbox`);
      iframeLocalStorage.removeItem(`current_conversation_id/sandbox//${url_project_id}`);
      window.location.reload();
    }

    // quota exceeded check, if the error message is not about question already answered
    if (
      errorStatus === StatusCodes.CONFLICT &&
      message !== ERROR_MESSAGES.QUESTION_ALREADY_ANSWERED
    ) {
      // Parse quota_full_link from error message (format: "error message||quota_full_link")
      const [_, quota_full_link] = message.split('||');

      let redirectUrl = '/?error_status=409';
      if (quota_full_link) {
        redirectUrl += `&quota_full_link=${encodeURIComponent(quota_full_link)}`;
      }
      window.location.href = redirectUrl;
    }

    return Promise.reject(error.response);
  },
);

export const chatRegister = async (
  projectId: string | undefined,
  allSearchParams: string | undefined,
): Promise<{ token: string }> => {
  if (!projectId) {
    throw new Error('Project ID is not defined');
  }
  const { data } = await axiosInstanceChat.get(`/chat/${projectId}${allSearchParams || ''}`);
  return data;
};

export const getChatHistory = async (conversation_id?: string): Promise<ChatHistory> => {
  const url = conversation_id ? `/chat/history/${conversation_id}` : '/chat/history';
  const { data } = await axiosInstanceChat.get(url);
  return data;
};

export default axiosInstanceChat;
